import type { ReactNode } from "react";

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <section className={`rounded-lg bg-white p-6 shadow-card sm:p-8 ${className}`}>{children}</section>;
}

export function CardHeader({
  title,
  description,
  action,
  badge,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  badge?: ReactNode;
}) {
  return (
    <div className="mb-6 flex items-start justify-between gap-4">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          {badge}
        </div>
        {description && <p className="text-sm text-gray-600">{description}</p>}
      </div>
      {action}
    </div>
  );
}

type Tone = "info" | "positive" | "negative" | "warning";

const toneClass: Record<Tone, string> = {
  info: "bg-primary-40 text-primary-900",
  positive: "bg-positive-light text-positive",
  negative: "bg-negative-light text-negative",
  warning: "bg-warning-light text-warning",
};

export function Alert({ tone = "info", children, className = "" }: { tone?: Tone; children: ReactNode; className?: string }) {
  return (
    <div role={tone === "negative" ? "alert" : "status"} className={`rounded-md px-4 py-3 text-sm ${toneClass[tone]} ${className}`}>
      {children}
    </div>
  );
}

export function Badge({ tone = "neutral", children }: { tone?: Tone | "neutral"; children: ReactNode }) {
  const cls =
    tone === "neutral"
      ? "bg-gray-300 text-gray-700"
      : tone === "info"
        ? "bg-primary-80 text-primary-900"
        : tone === "positive"
          ? "bg-positive-light text-positive"
          : tone === "negative"
            ? "bg-negative-light text-negative"
            : "bg-warning-light text-warning";
  return <span className={`inline-flex h-6 shrink-0 items-center whitespace-nowrap rounded-full px-2.5 text-xs font-semibold ${cls}`}>{children}</span>;
}

export function Divider({ label }: { label?: string }) {
  if (!label) return <hr className="border-gray-400" />;
  return (
    <div className="flex items-center gap-3 text-xs text-gray-500">
      <span className="h-px flex-1 bg-gray-400" />
      {label}
      <span className="h-px flex-1 bg-gray-400" />
    </div>
  );
}

/** 정의 목록 한 줄. 업체 정보 표시용. */
export function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[112px_1fr] gap-3 py-2.5 text-sm sm:grid-cols-[140px_1fr]">
      <dt className="text-gray-600">{label}</dt>
      <dd className="text-gray-900">{children || <span className="text-gray-500">없음</span>}</dd>
    </div>
  );
}

export function ProgressBar({ value, max }: { value: number; max: number }) {
  const pct = max === 0 ? 0 : Math.round((value / max) * 100);
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-300" role="progressbar" aria-valuenow={value} aria-valuemax={max}>
      <div className="h-full rounded-full bg-primary-900 transition-all" style={{ width: `${pct}%` }} />
    </div>
  );
}
